import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Heart, Trash2, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";

const WishlistPage = () => {
  const { t } = useTranslation();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`);
      setWishlist(response.data);
    } catch (error) {
      toast.error("Failed to load wishlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWishlist();
  }, []);

  const removeFromWishlist = async (wishlistId) => {
    try {
      await axios.delete(`${API}/wishlist/${wishlistId}`);
      setWishlist(wishlist.filter(w => w.id !== wishlistId));
      toast.success("Removed from wishlist");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const addToCart = async (item) => {
    try {
      await axios.post(`${API}/cart/add`, {
        product_id: item.product_id,
        quantity: 1,
      });
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <Heart className="w-8 h-8 text-yellow-500" />
            <h1 className="text-3xl font-black text-white font-['Outfit']">{t("wishlist.title")}</h1>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : wishlist.length === 0 ? (
            <div className="text-center py-20">
              <Heart className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">{t("wishlist.empty")}</h2>
              <p className="text-slate-400 mb-6">Save items you love to your wishlist</p>
              <Link to="/store" className="btn-primary px-6 py-3 inline-block">
                Browse Store
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {wishlist.map((item) => (
                <div key={item.id} className="card-surface overflow-hidden" data-testid={`wishlist-item-${item.id}`}>
                  <Link to={`/store/product/${item.product_id}`}>
                    <img 
                      src={item.product_image || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500"}
                      alt={item.product_name}
                      className="w-full aspect-square object-cover"
                    />
                  </Link>
                  <div className="p-4">
                    <Link to={`/store/product/${item.product_id}`}>
                      <h3 className="text-white font-medium hover:text-yellow-500 transition-colors">
                        {item.product_name}
                      </h3>
                    </Link>
                    <p className="text-lg font-bold text-yellow-500 mt-2">R{item.price.toFixed(2)}</p>
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => addToCart(item)}
                        className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-2"
                        data-testid={`add-cart-${item.id}`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Add to Cart
                      </Button>
                      <Button
                        onClick={() => removeFromWishlist(item.id)}
                        variant="outline"
                        className="btn-secondary px-3"
                        data-testid={`remove-wishlist-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WishlistPage;
